import { useMemo, useRef, useState } from 'react'
import { MAX_RESUME_BYTES, RESUME_ACCEPTED_FILE_EXTENSIONS } from '../lib/constants'
import { ExternalLinkIcon, FileIcon, TrashIcon, UploadIcon } from './Icons'
import Modal from './Modal'

const RESUME_ACCEPT_VALUE = RESUME_ACCEPTED_FILE_EXTENSIONS.join(',')

export default function ResourceModal({ resource, saving, onClose, onOpenResume, onSave }) {
  const fileInputRef = useRef(null)
  const [name, setName] = useState(resource?.name || '')
  const [role, setRole] = useState(resource?.role || '')
  const [email, setEmail] = useState(resource?.email || '')
  const [startDate, setStartDate] = useState(resource?.start_date || '')
  const [vendorName, setVendorName] = useState(resource?.vendor_name || '')
  const [comments, setComments] = useState(resource?.comments || '')
  const [resumeFile, setResumeFile] = useState(null)
  const [removeResume, setRemoveResume] = useState(false)
  const [error, setError] = useState('')

  const editing = Boolean(resource?.id)
  const resumeLabel = useMemo(() => {
    if (resumeFile) return resumeFile.name
    if (resource?.resume_label && !removeResume) return resource.resume_label
    return 'No resume selected'
  }, [removeResume, resource?.resume_label, resumeFile])

  function handleResumeSelected(event) {
    const file = event.target.files?.[0] || null
    setError('')
    if (!file) return
    if (file.size > MAX_RESUME_BYTES) {
      setError(`“${file.name}” is larger than 6 MB.`)
      event.target.value = ''
      return
    }
    setResumeFile(file)
    setRemoveResume(false)
    event.target.value = ''
  }

  function clearResume() {
    setResumeFile(null)
    setRemoveResume(Boolean(resource?.resume_storage_path || resource?.resume_label))
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setError('')

    if (!name.trim()) {
      setError('Resource name is required.')
      return
    }

    if (email && !/^\S+@\S+\.\S+$/.test(email)) {
      setError('Enter a valid email address or leave it blank.')
      return
    }

    if (resumeFile && resumeFile.size > MAX_RESUME_BYTES) {
      setError(`“${resumeFile.name}” is larger than 6 MB.`)
      return
    }

    try {
      await onSave({
        name,
        role,
        email,
        startDate,
        vendorName,
        comments,
        resumeFile,
        removeResume,
      })
    } catch (saveError) {
      setError(saveError.message || `The resource could not be ${editing ? 'updated' : 'added'}.`)
    }
  }

  return (
    <Modal
      className="resource-modal resource-modal--profile"
      onClose={onClose}
      subtitle={editing ? 'Update the resource profile and replace the resume when needed.' : 'Add a person and their engagement details to TimeGrid.'}
      title={editing ? 'Edit resource' : 'Add resource'}
    >
      <form onSubmit={handleSubmit}>
        <div className="modal-body form-stack resource-profile-form">
          <div className="resource-form-grid">
            <label className="field-label" htmlFor="resource-name">
              Resource name
              <input
                autoFocus
                id="resource-name"
                maxLength="120"
                onChange={(event) => setName(event.target.value)}
                placeholder="e.g. Priya Sharma"
                required
                type="text"
                value={name}
              />
            </label>

            <label className="field-label" htmlFor="resource-role">
              Role <span className="field-optional">optional</span>
              <input
                id="resource-role"
                maxLength="120"
                onChange={(event) => setRole(event.target.value)}
                placeholder="e.g. Consultant"
                type="text"
                value={role}
              />
            </label>

            <label className="field-label" htmlFor="resource-email">
              Email <span className="field-optional">optional</span>
              <input
                id="resource-email"
                maxLength="254"
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@company.com"
                type="email"
                value={email}
              />
            </label>

            <label className="field-label" htmlFor="resource-start-date">
              Start date <span className="field-optional">optional</span>
              <input
                id="resource-start-date"
                onChange={(event) => setStartDate(event.target.value)}
                type="date"
                value={startDate}
              />
            </label>

            <label className="field-label" htmlFor="resource-vendor">
              Vendor name <span className="field-optional">optional</span>
              <input
                id="resource-vendor"
                maxLength="160"
                onChange={(event) => setVendorName(event.target.value)}
                placeholder="e.g. Acme Staffing"
                type="text"
                value={vendorName}
              />
            </label>

            <div className="field-label resource-resume-field">
              Resume <span className="field-optional">optional · max 6 MB</span>
              <div className={`resume-picker ${resumeFile || (resource?.resume_label && !removeResume) ? 'has-file' : ''}`}>
                <span className="resume-picker-icon"><FileIcon size={18} /></span>
                <span className="resume-picker-copy">
                  <strong title={resumeLabel}>{resumeLabel}</strong>
                  <small>PDF, Word, RTF or text file</small>
                </span>
                {editing && resource?.resume_storage_path && !resumeFile && !removeResume ? (
                  <button
                    aria-label="Open current resume"
                    className="icon-button"
                    onClick={() => onOpenResume?.(resource)}
                    title="Open current resume"
                    type="button"
                  >
                    <ExternalLinkIcon size={16} />
                  </button>
                ) : null}
                {resumeFile || (resource?.resume_label && !removeResume) ? (
                  <button
                    aria-label="Remove resume"
                    className="icon-button icon-button--danger"
                    onClick={clearResume}
                    title={resumeFile ? 'Clear selected file' : 'Remove current resume'}
                    type="button"
                  >
                    <TrashIcon size={16} />
                  </button>
                ) : null}
              </div>
              <label className="button button--secondary resource-resume-upload">
                <UploadIcon size={16} />
                {resource?.resume_label ? 'Replace resume' : 'Choose resume'}
                <input
                  accept={RESUME_ACCEPT_VALUE}
                  onChange={handleResumeSelected}
                  ref={fileInputRef}
                  type="file"
                />
              </label>
              {removeResume ? <small className="field-help field-help--warning">The current resume will be removed when you save.</small> : null}
            </div>
          </div>

          <label className="field-label" htmlFor="resource-comments">
            Comments <span className="field-optional">optional</span>
            <textarea
              id="resource-comments"
              maxLength="2000"
              onChange={(event) => setComments(event.target.value)}
              placeholder="Engagement notes, onboarding details, skills, or other comments"
              rows="4"
              value={comments}
            />
          </label>

          {error ? <p className="form-error">{error}</p> : null}
        </div>

        <footer className="modal-footer">
          <button className="button button--secondary" disabled={saving} onClick={onClose} type="button">
            Cancel
          </button>
          <button className="button button--primary" disabled={saving} type="submit">
            {saving ? (editing ? 'Saving…' : 'Adding…') : (editing ? 'Save changes' : 'Add resource')}
          </button>
        </footer>
      </form>
    </Modal>
  )
}
